import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Trash2, Star, BadgeCheck } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";

/**
 * Wishlist-specific card used only on the My Account → Wishlist page.
 * Kept separate from the main `ProductCard` so we don't alter the shop-facing
 * component (per project constraint).
 */
export default function WishlistCard({ product }) {
  const p = product || {};
  const { addToCart } = useCart();
  const { toggleWishlist } = useWishlist();

  const inStock = p.in_stock !== false;
  const savePct =
    p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const handleMove = async () => {
    if (!inStock) return;
    await addToCart(p.id, 1);
    toggleWishlist(p);
  };

  const handleRemove = () => toggleWishlist(p);

  return (
    <div
      data-testid={`wishlist-card-${p.id}`}
      className="group relative rounded-2xl border border-gray-100 bg-white overflow-hidden hover:shadow-md hover:border-[#4B2AAD]/20 transition-all"
    >
      {savePct > 0 && (
        <span className="absolute top-3 left-3 z-10 rounded-md bg-emerald-500 text-white text-[10px] font-bold px-2 py-1">
          {savePct}% OFF
        </span>
      )}
      <button
        type="button"
        onClick={handleRemove}
        aria-label="Remove from wishlist"
        data-testid={`wishlist-heart-${p.id}`}
        className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-[#F43F5E] hover:bg-[#F43F5E]/10"
      >
        <Heart className="h-4 w-4 fill-[#F43F5E]" />
      </button>

      <Link to={`/product/${p.id}`} className="block">
        <div className="aspect-square bg-[#F5F3FB] flex items-center justify-center p-3">
          <img
            src={p.image || "/product-placeholder.svg"}
            alt={p.name}
            className="w-full h-full object-contain"
            onError={(e) => {
              e.currentTarget.src = "/product-placeholder.svg";
            }}
          />
        </div>
      </Link>

      <div className="p-3 space-y-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-0.5 ${
            inStock
              ? "bg-[#F3EFFC] text-[#4B2AAD]"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          <BadgeCheck className="h-3 w-3" />
          {inStock ? "In Stock" : "Out of Stock"}
        </span>

        <Link
          to={`/product/${p.id}`}
          className="block text-sm font-bold text-gray-900 line-clamp-2 min-h-[36px] hover:text-[#4B2AAD]"
        >
          {p.name}
        </Link>

        {p.short_description && (
          <p className="text-[11px] text-gray-500 line-clamp-1">
            {p.short_description}
          </p>
        )}

        {p.rating > 0 && (
          <div className="flex items-center gap-1 text-[11px] text-gray-600">
            <Star className="h-3 w-3 text-[#FFB020] fill-[#FFB020]" />
            <span className="font-semibold">{p.rating.toFixed(1)}</span>
            {p.reviews > 0 && (
              <span className="text-gray-400">
                ({p.reviews.toLocaleString()})
              </span>
            )}
          </div>
        )}

        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-base font-extrabold text-gray-900">
            ₹{(p.price || 0).toLocaleString()}
          </span>
          {p.mrp > p.price && (
            <span className="text-xs text-gray-400 line-through">
              ₹{p.mrp.toLocaleString()}
            </span>
          )}
        </div>

        <div className="pt-1 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleMove}
            disabled={!inStock}
            data-testid={`wishlist-move-${p.id}`}
            className="inline-flex items-center justify-center gap-1 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-[11px] font-bold px-2 py-2"
          >
            <ShoppingCart className="h-3.5 w-3.5" /> Move to Cart
          </button>
          <button
            type="button"
            onClick={handleRemove}
            data-testid={`wishlist-remove-${p.id}`}
            className="inline-flex items-center justify-center gap-1 rounded-xl border border-gray-200 hover:border-red-300 hover:text-red-500 text-gray-600 text-[11px] font-bold px-2 py-2"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}
